import { z } from "zod";
import { fcfa, useApiEnveloppe } from "../../api/hooks";
import { AfficheNotation } from "../../composants/AfficheNotation";
import { EtatChargement, EtatErreur, ListeAvis } from "../../composants/Avis";
import { Tableau } from "../../composants/Tableau";
import type { EcranProps } from "../types";

const ConstatSchema = z.object({
  code: z.string(),
  domaine: z.string(),
  gravite: z.string(),
  malus: z.number(),
  libelle: z.string(),
  reference: z.string().nullable(),
  action: z.string().nullable(),
  responsable: z.string().nullable(),
  exposition: z.number(),
});

const Schema = z.object({
  evaluable: z.boolean(),
  noteGlobale: z.number().nullable(),
  cote: z.string(),
  motif: z.string().nullable(),
  visaFavorable: z.boolean(),
  affichage: z.string(),
  domaines: z.array(
    z.object({
      domaine: z.string(),
      libelle: z.string(),
      ponderation: z.number(),
      note: z.number(),
      nbConstats: z.number(),
    }),
  ),
  constats: z.array(ConstatSchema),
  sources: z.object({
    journalTraite: z.boolean(),
    registreFacturation: z.boolean(),
    releveBancaire: z.boolean(),
    nombre: z.number(),
  }),
});

export function Reporting({ dossierId, annee, mois }: EcranProps) {
  const q = useApiEnveloppe(
    ["notation", dossierId, annee, mois],
    `/dossiers/${dossierId}/notation/${annee}/${mois}`,
    Schema,
  );

  if (q.isLoading) return <EtatChargement quoi="de la notation" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <p className="a-faire">Aucune donnée.</p>;

  const n = q.data.valeur;

  return (
    <div>
      <AfficheNotation notation={n} />

      <section>
        <h2>Sources exploitées</h2>
        <p className="resume">
          La notation ne se prononce qu'à partir de sources réelles. Sans matière,
          la cote reste <strong>NON ÉVALUÉ</strong> — une agence de notation ne
          délivre pas de cote sans matière.
        </p>
        <ul className="sources">
          <li className={n.sources.journalTraite ? "presente" : "absente"}>
            Journal traité {n.sources.journalTraite ? "— présent" : "— absent"}
          </li>
          <li className={n.sources.registreFacturation ? "presente" : "absente"}>
            Registre de facturation {n.sources.registreFacturation ? "— présent" : "— absent"}
          </li>
          <li className={n.sources.releveBancaire ? "presente" : "absente"}>
            Relevé bancaire {n.sources.releveBancaire ? "— présent" : "— absent"}
          </li>
        </ul>
      </section>

      <section>
        <h2>Opinion du réviseur</h2>
        <p className={n.visaFavorable ? "visa visa--emis" : "visa visa--reserve"}>
          {n.visaFavorable
            ? `Visa émis — la cote ${n.cote} est au moins égale à BBB.`
            : n.evaluable
              ? `Visa réservé — la cote ${n.cote} est inférieure à BBB.`
              : "Visa réservé — aucune cote ne peut être prononcée sans matière."}
        </p>
      </section>

      <section>
        <h2>Constats et plan d'action</h2>
        <Tableau
          colonnes={[
            { cle: "code", titre: "Code", rendu: (c) => <code>{c.code}</code> },
            { cle: "dom", titre: "Domaine", rendu: (c) => c.domaine },
            { cle: "grav", titre: "Gravité", rendu: (c) => `${c.gravite} (−${c.malus})` },
            { cle: "lib", titre: "Constat", rendu: (c) => c.libelle },
            { cle: "ref", titre: "Référence", rendu: (c) => c.reference ?? "—" },
            { cle: "act", titre: "Action", rendu: (c) => c.action ?? "—" },
            { cle: "resp", titre: "Responsable", rendu: (c) => c.responsable ?? "—" },
            {
              cle: "expo",
              titre: "Exposition",
              montant: true,
              rendu: (c) => (c.exposition > 0 ? fcfa(c.exposition) : "—"),
            },
          ]}
          lignes={n.constats}
          cle={(c, i) => `${c.code}-${i}`}
          vide="Aucun constat sur la période."
        />
      </section>

      <section>
        <h2>Exports</h2>
        <p className="resume">
          Les livrables sortent du <strong>générateur officiel</strong>, seule voie
          autorisée. Le contrôle qualité est bloquant : le fichier ne sort que si
          la charte est appliquée et qu'aucun symbole Markdown ne subsiste.
        </p>
        <div className="actions">
          <a
            className="bouton"
            href={`/api/dossiers/${dossierId}/livrables/reporting?annee=${annee}&mois=${mois}`}
          >
            Rapport de reporting (PDF)
          </a>
          <a
            className="bouton"
            href={`/api/dossiers/${dossierId}/livrables/revision?annee=${annee}&mois=${mois}`}
          >
            Rapport de révision et note de conformité (PDF)
          </a>
        </div>
      </section>

      <ListeAvis avis={q.data.avis} titre="Observations des moteurs" />
    </div>
  );
}
